<script setup lang="ts">
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import type { Icon } from "@/model/icon";
import { injectStrict } from "@/utils/vue";

defineProps<{
  title: string;
  subtitle: string;
  primaryLabel: string;
  primaryIcon: Icon;
  primaryDisabled?: boolean;
  destructive?: boolean;
}>();

const emit = defineEmits<{ primary: [] }>();

const dropdown = injectStrict(dropdownKey);

// Close synchronously before bubbling so a double-click on primary can't fire twice.
function onPrimary() {
  dropdown.close();
  emit("primary");
}
</script>

<template>
  <div
    v-focus-trap
    class="bulk-action-modal"
    role="alertdialog"
    aria-labelledby="bulk-action-title"
    @keydown.tab.stop
  >
    <div class="content">
      <header class="header">
        <h2 id="bulk-action-title" class="title">{{ title }}</h2>
        <p class="subtitle">{{ subtitle }}</p>
      </header>

      <!-- Slot for actionable items -->
      <slot />

      <div class="actions">
        <!-- Primary action -->
        <BaseButton
          :disabled="primaryDisabled"
          full-width
          :color="destructive ? 'destructive' : 'primary'"
          :icon-before="primaryIcon"
          @click="onPrimary"
        >
          {{ primaryLabel }}
        </BaseButton>
        <!-- Cancel button -->
        <BaseButton v-autofocus full-width variant="outlined" color="grey" @click="dropdown.close">
          {{ $t("general.cancel") }}
        </BaseButton>
      </div>
    </div>

    <!-- Non-actionable items (eg. selected items which cannot be deleted) -->
    <section v-if="$slots.blocked" class="blocked-zone">
      <slot name="blocked" />
    </section>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "./delete-modal-shared" as shared;
@use "@/styles/mixins/typography";

$padding: 16px;

.bulk-action-modal {
  @include shared.delete-modal;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  .content {
    padding: $padding;
    display: flex;
    flex-direction: column;
    gap: $padding;
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .title {
      margin: 0;
    }

    .subtitle {
      @include typography.small("medium");
    }
  }

  // Bleeds to full-width
  .blocked-zone {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: $padding;
    background-color: colors.$background-grey-light;
    border-top: 1px solid colors.$border-subtle;
    color: colors.$text-subtle;

    // Separate multiple items with a border
    > *:not(:first-child) {
      border-top: 1px solid colors.$border-subtle;
      padding-top: 12px;
    }
  }
}
</style>
